// FingerprintJS integration — generates stable visitor IDs from browser signals.
// Accuracy: ~40-70% on mobile (Samsung/Tecno/Infinix), higher on desktop.
// This is a WEAK signal — phone number is the primary identifier.
//
// FingerprintJS (~30KB gzipped) is dynamically imported so it does NOT block
// the initial JS parse. It is loaded on-demand the first time getVisitorId() runs.

export const VISITOR_ID_KEY = "djayma_visitor_id";

// Cached agent promise — resolved once per session
let fpPromise: Promise<{ get(): Promise<{ visitorId: string }> }> | null = null;

function getFpAgent(): Promise<{ get(): Promise<{ visitorId: string }> }> {
  if (!fpPromise) {
    // Dynamic import defers the 30KB parse cost until the agent is first needed
    fpPromise = import("@fingerprintjs/fingerprintjs").then((FingerprintJS) =>
      FingerprintJS.default.load()
    );
  }
  return fpPromise;
}

/**
 * Returns a stable visitor ID. Uses localStorage cache first,
 * falls back to FingerprintJS computation, and finally generates
 * a random fallback if FingerprintJS fails entirely.
 */
export async function getVisitorId(): Promise<string> {
  // 1. Check cache
  const cached = localStorage.getItem(VISITOR_ID_KEY);
  if (cached) {
    return cached;
  }

  // 2. Compute via FingerprintJS
  try {
    const agent = await getFpAgent();
    const result = await agent.get();
    const visitorId = result.visitorId;
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
    return visitorId;
  } catch (error) {
    // 3. Fallback — random ID prefixed for identification
    console.warn("[fingerprint] FingerprintJS failed, using fallback:", error);
    const fallbackId = `fallback_${crypto.randomUUID()}`;
    localStorage.setItem(VISITOR_ID_KEY, fallbackId);
    return fallbackId;
  }
}

/**
 * Clears the stored visitor ID. Used when consent is withdrawn.
 */
export function clearVisitorId(): void {
  localStorage.removeItem(VISITOR_ID_KEY);
  fpPromise = null;
}

/**
 * Returns device info for the visitor registration API.
 */
export function getDeviceInfo(): Record<string, unknown> {
  return {
    os: navigator.platform,
    browser: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    touch: navigator.maxTouchPoints > 0,
  };
}
